import _ from "lodash";
import React, { useReducer } from "react";
import { useTranslation } from "react-i18next";
import i18next from "src/i18n/config";

import { AddMemberDialog } from "@dashboard/shared/components/AddMemberDialog";
import { PaneElement } from "@dashboard/shared/components/SidePanel/shared/PaneElement";
import { useCurrentSubscriptionExpired, useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { Group, GroupMember } from "@devices/types";
import { memberSubject, PermissionService } from "@services/permissionService/permissionService";
import { SecondaryButton } from "@shared/CustomButton";
import { MinimalTable, MinimalTableHeader, MinimalTableRow } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { TableHeaderRows } from "@shared/table/TableHeader";
import { TableRowCells } from "@shared/table/TableRowCells";
import { ColumnDef, getCoreRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";

import { ActionCell, EmailCell, RoleCell } from "./Cells";

export type TableData = {
    isWorkspaceOnly: boolean;
    groupId: number;
    id: number;
    status: string;
    email: string;
    role: GroupMember["role"];
};

const columns: ColumnDef<TableData, unknown>[] = [
    {
        id: "email",
        header: i18next.t("common:label.email"),
        cell: EmailCell,
        enableSorting: true,
        accessorKey: "email",
        sortingFn: "alphanumeric",
    },
    {
        id: "role",
        header: i18next.t("common:label.role"),
        cell: RoleCell,
        enableSorting: true,
        accessorKey: "role",
        sortingFn: "alphanumeric",
    },
    {
        id: "action",
        header: "",
        cell: ActionCell,
    },
];

const MemberTab = (props: Group): React.JSX.Element => {
    const [dialogOpen, setDialogOpen] = useReducer((state) => !state, false);

    const { t } = useTranslation(["sidepanels"]);

    const isExpired = useCurrentSubscriptionExpired();
    const { data: workspace } = useCurrentWorkspace();

    const { groupAbility } = PermissionService();
    const ability = groupAbility(props, workspace);
    const mayManageMember = ability.can("manage", "Member");
    const mayInviteMember = ability.can("create", "Member");

    const allMembers = React.useMemo(() => {
        return _.unionBy(props.members, workspace?.members, "email").map((member) => {
            const isGroupMember = props.members.some((groupMember) => groupMember.email === member.email);
            return { ...member, isWorkspaceOnly: !isGroupMember, groupId: props.id };
        });
    }, [props.members, workspace?.members, props.id]);

    const table = useReactTable({
        data: allMembers,
        columns: columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const getAddBtnTitle = () => {
        if (!mayManageMember) {
            return { title: t("sidepanels:groupPanel.tooltip.groupRole") };
        }
        if (isExpired) {
            return { title: t("sidepanels:groupPanel.tooltip.subscriptionExpired") };
        }
        return { title: t("sidepanels:groupPanel.tooltip.addMember") };
    };

    return (
        <PaneElement.Container>
            <PaneElement.TitleRow title={t("sidepanels:groupPanel.membersTab.groupMemberships")}>
                <SecondaryButton onClick={setDialogOpen} tooltipProps={getAddBtnTitle()} disabled={!mayInviteMember}>
                    <i className="fa-solid fa-user-plus" />
                </SecondaryButton>
            </PaneElement.TitleRow>
            <PaneElement.TableLayout contentDeps={[allMembers.length]}>
                <MinimalTable stickyHeader>
                    <MinimalTableHeader>
                        <TableHeaderRows headerGroups={table.getHeaderGroups()} />
                    </MinimalTableHeader>
                    <TableBody table={table} noDataMessage={t("sidepanels:groupPanel.membersTab.noMembers")}>
                        {table.getRowModel().rows.map((row) => (
                            <MinimalTableRow key={row.id}>
                                <TableRowCells row={row} />
                            </MinimalTableRow>
                        ))}
                    </TableBody>
                </MinimalTable>
            </PaneElement.TableLayout>
            <AddMemberDialog
                name="group"
                open={dialogOpen}
                onClose={setDialogOpen}
                id={props.id}
                canAssignOwner={ability.can("manage", memberSubject("Owner"))}
            />
        </PaneElement.Container>
    );
};

export default MemberTab;
